import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navigation from '../components/Navigation';
import MovieSearch, { SearchType } from '../components/MovieSearch';
import MovieCard from '../components/MovieCard';
import { movieApi, Movie, ApiStats, handleApiError } from '../services/api';

const Home: React.FC = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState<ApiStats | null>(null);
  const [randomMovie, setRandomMovie] = useState<Movie | null>(null);
  const [isLoadingStats, setIsLoadingStats] = useState(true);
  const [isLoadingRandom, setIsLoadingRandom] = useState(false);

  useEffect(() => {
    fetchStats();
    fetchRandomMovie();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await movieApi.getStats();
      setStats(response);
    } catch (error) {
      console.error('Failed to fetch stats:', handleApiError(error));
    } finally {
      setIsLoadingStats(false);
    }
  };

  const fetchRandomMovie = async () => {
    setIsLoadingRandom(true);
    try {
      const response = await movieApi.getRandomMovie();
      setRandomMovie(response);
    } catch (error) {
      console.error('Failed to fetch random movie:', handleApiError(error));
    } finally {
      setIsLoadingRandom(false);
    }
  };

  const handleMovieSelect = (movie: Movie) => {
    navigate(`/movie/${movie.id}`);
  };

  const handleSearch = (query: string, type: SearchType) => {
    switch (type) {
      case 'actor':
        navigate(`/browse?tab=actors&actor=${encodeURIComponent(query)}`);
        break;
      case 'director':
        navigate(`/browse?tab=directors&director=${encodeURIComponent(query)}`);
        break;
      case 'genre':
        navigate(`/browse?tab=genres&genre=${encodeURIComponent(query)}`);
        break;
      default:
        // For movies, this shouldn't happen as movies are handled by onMovieSelect
        break;
    }
  };

  const handleRandomMovieClick = () => {
    if (randomMovie) {
      navigate(`/movie/${randomMovie.id}`);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50">
      <Navigation />

      {/* Hero Section */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white">
        <div className="container mx-auto px-4 py-16">
          <div className="text-center mb-12">
            <h1 className="text-5xl font-bold mb-4">
              🎬 Movie Recommendations
            </h1>
            <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
              Discover movies similar to your favorites using advanced script analysis and Word2Vec embeddings
            </p>
          </div>

          {/* Search Section */}
          <div className="max-w-2xl mx-auto">
            <MovieSearch 
              onMovieSelect={handleMovieSelect}
              onSearch={handleSearch}
              className="mb-6"
            />
            <p className="text-center text-blue-100 text-sm">
              Search from {stats?.total_movies?.toLocaleString() || '1,200+'} movies, actors, directors, and genres
            </p>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-12">
        {/* Statistics Section */}
        <div className="mb-12">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-8">
            Our Movie Database
          </h2>
          
          {isLoadingStats ? (
            <div className="flex justify-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : stats ? (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              <div className="bg-white rounded-lg shadow-md p-6 text-center">
                <div className="text-3xl font-bold text-blue-600 mb-2">
                  {stats.total_movies.toLocaleString()}
                </div>
                <div className="text-gray-600">Movies</div>
              </div>
              
              <div className="bg-white rounded-lg shadow-md p-6 text-center">
                <div className="text-3xl font-bold text-purple-600 mb-2">
                  {stats.total_genres}
                </div>
                <div className="text-gray-600">Genres</div>
              </div>
              
              <div className="bg-white rounded-lg shadow-md p-6 text-center">
                <div className="text-3xl font-bold text-green-600 mb-2">
                  {stats.total_directors.toLocaleString()}
                </div>
                <div className="text-gray-600">Directors</div>
              </div>
              
              <div className="bg-white rounded-lg shadow-md p-6 text-center">
                <div className="text-3xl font-bold text-red-600 mb-2">
                  {stats.script_embeddings?.toLocaleString() || 'N/A'}
                </div>
                <div className="text-gray-600">AI Embeddings</div>
              </div>
            </div>
          ) : null}
        </div>

        {/* Random Movie Section */}
        <div className="mb-12">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Feeling Lucky?
            </h2>
            <p className="text-gray-600 mb-6">
              Let us pick a random movie for you to explore
            </p>
            <button
              onClick={fetchRandomMovie}
              disabled={isLoadingRandom}
              className="px-6 py-3 bg-gradient-to-r from-purple-500 to-purple-600 text-white rounded-lg hover:from-purple-600 hover:to-purple-700 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoadingRandom ? (
                <span className="flex items-center gap-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  Loading...
                </span>
              ) : (
                '🎲 Get Random Movie'
              )}
            </button>
          </div>

          {randomMovie && (
            <div className="max-w-sm mx-auto">
              <div className="text-center mb-4">
                <span className="inline-block px-4 py-2 bg-purple-100 text-purple-800 text-sm font-medium rounded-full mb-4">
                  🎲 Random Pick
                </span>
              </div>
              <MovieCard
                movie={randomMovie}
                onClick={handleRandomMovieClick}
                size="large"
                className="mx-auto border-2 border-purple-200 hover:border-purple-400"
              />
              <div className="text-center mt-4">
                <span className="text-purple-600 font-medium">
                  Click to view details and get recommendations →
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Features Section */}
        <div className="mb-12">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-8">
            How It Works
          </h2>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="bg-blue-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Search Movies</h3>
              <p className="text-gray-600">
                Search through our database of {stats?.total_movies?.toLocaleString() || '1,200+'} movies with comprehensive metadata
              </p>
            </div>
            
            <div className="text-center">
              <div className="bg-purple-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">AI Analysis</h3>
              <p className="text-gray-600">
                Our Word2Vec model analyzes movie scripts to understand content similarity beyond just genres
              </p>
            </div>
            
            <div className="text-center">
              <div className="bg-green-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Get Recommendations</h3>
              <p className="text-gray-600">
                Receive personalized movie recommendations with similarity scores and detailed information
              </p>
            </div>
          </div>
        </div>

        {/* Browse Section */}
        <div className="text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">
            Explore Our Collection
          </h2>
          <div className="flex justify-center gap-4 flex-wrap">
            <button
              onClick={() => navigate('/browse')}
              className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              🎭 Browse by Genre
            </button>
            <button
              onClick={() => navigate('/browse?tab=directors')}
              className="px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
            >
              🎬 Browse by Director
            </button>
            <button
              onClick={() => navigate('/browse?tab=actors')}
              className="px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
            >
              🎭 Browse by Actor
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Home;